import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { AudiencePicker } from "@/admin/AudiencePicker";
import { fmtDateLong } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, Megaphone, Pin, PinOff, Users, MoreVertical, Pencil, Copy, Send, Archive,
  Trash2, AlertTriangle, CalendarClock, ExternalLink, ArchiveRestore,
} from "lucide-react";

const STATUS_STYLE = {
  published: "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]",
  scheduled: "bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))]",
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  archived: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] line-through",
};

const blank = () => ({
  title: "", body: "", cta_label: "", cta_url: "", audience_spec: { type: "EVERYONE" },
  pinned: false, priority: "normal", status: "draft", publish_at: "", expires_at: "",
});

export default function AnnouncementsAdmin() {
  const { eventId } = useAdminEvent();
  const [anns, setAnns] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.mgrAnnouncements(eventId).then((r) => setAnns(r.announcements || [])).catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <PageIntro>Create, schedule and pin announcements. Audience is resolved by the shared engine so each message reaches only eligible attendees. Important or pinned messages surface on the attendee Home. No push notifications are sent.</PageIntro>
        <Button data-testid="create-announcement-btn" onClick={() => setEditing(blank())}><Plus className="h-4 w-4 mr-1" /> Create Announcement</Button>
      </div>

      <div className="space-y-3">
        {anns.length === 0 && <div className="premium-card p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No announcements yet.</div>}
        {anns.map((a) => {
          const important = a.priority === "important" || a.priority === "high";
          return (
            <div key={a.id} className="premium-card p-4" data-testid={`ann-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${important ? "bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(var(--pc-gold))]" : "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]"}`}><Megaphone className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[a.status] || STATUS_STYLE.draft}`}>{a.status || "draft"}</span>
                      {important && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))]"><AlertTriangle className="h-3 w-3" /> Important</span>}
                      <span className="font-semibold text-[hsl(var(--primary))] truncate">{a.title}</span>
                      {a.pinned && <Pin className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" />}
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{a.body}</p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-[hsl(var(--muted-foreground))]">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--primary))] border border-[hsl(var(--pc-gold)/0.4)] px-2.5 py-1 font-medium"><Users className="h-3 w-3" /> {a.audience_label}{a.audience_count != null ? ` · ${a.audience_count}` : ""}</span>
                      {a.publish_at && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {fmtDateLong(a.publish_at)}</span>}
                      {a.cta_label && <span className="inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> {a.cta_label}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`ann-edit-${a.id}`} onClick={() => setEditing(a)}><Pencil className="h-3.5 w-3.5" /></Button>
                  {a.status !== "published" && a.status !== "archived" && <Button size="sm" data-testid={`ann-publish-${a.id}`} onClick={() => act(() => api.mgrAnnPublish(eventId, a.id), "Published")}><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`ann-more-${a.id}`}><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => act(() => api.mgrAnnPin(eventId, a.id, !a.pinned), a.pinned ? "Unpinned" : "Pinned")}>{a.pinned ? <><PinOff className="h-4 w-4 mr-2" /> Unpin</> : <><Pin className="h-4 w-4 mr-2" /> Pin</>}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => act(() => api.mgrAnnDuplicate(eventId, a.id), "Duplicated")}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                      {a.status !== "archived"
                        ? <DropdownMenuItem onClick={() => act(() => api.mgrAnnArchive(eventId, a.id, true), "Archived")}><Archive className="h-4 w-4 mr-2" /> Archive / unpublish</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => act(() => api.mgrAnnArchive(eventId, a.id, false), "Restored")}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>}
                      {(a.status === "draft" || !a.status) && <DropdownMenuItem className="text-red-600" onClick={() => act(() => api.mgrAnnDelete(eventId, a.id), "Deleted")}><Trash2 className="h-4 w-4 mr-2" /> Delete draft</DropdownMenuItem>}
                      {a.status && a.status !== "draft" && <div className="px-2 py-1.5 text-[10px] text-[hsl(var(--muted-foreground))] max-w-[13rem]">Published announcements can't be deleted — archive removes them from attendees while preserving history.</div>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && <AnnouncementEditor eventId={eventId} announcement={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AnnouncementEditor({ eventId, announcement, onClose, onSaved }) {
  const isNew = !announcement.id;
  const [f, setF] = useState({ ...blank(), ...announcement, priority: (announcement.priority === "high" ? "important" : announcement.priority) || "normal" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async (andSchedule) => {
    if (!f.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = {
        title: f.title, body: f.body, cta_label: f.cta_label, cta_url: f.cta_url,
        audience_spec: f.audience_spec, pinned: f.pinned, priority: f.priority,
        publish_at: f.publish_at || null, expires_at: f.expires_at || null,
      };
      let saved;
      if (isNew) saved = await api.mgrAnnCreate(eventId, body);
      else { saved = await api.mgrAnnUpdate(eventId, announcement.id, body); }
      if (andSchedule) {
        if (!f.publish_at) { toast.error("Set a publish date/time to schedule"); setSaving(false); return; }
        await api.mgrAnnSchedule(eventId, saved.id || announcement.id, { publish_at: f.publish_at, expires_at: f.expires_at || null });
      }
      toast.success(isNew ? "Announcement created" : "Announcement updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="announcement-editor">
        <DialogHeader><DialogTitle>{isNew ? "Create Announcement" : "Edit Announcement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="ann-title" value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div><Label>Message</Label><Textarea rows={3} data-testid="ann-body" value={f.body} onChange={(e) => set("body", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>CTA label (optional)</Label><Input value={f.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="e.g. View details" /></div>
            <div><Label>CTA link (optional)</Label><Input value={f.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://…" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="ann-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="important">Important</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch id="pin" checked={!!f.pinned} onCheckedChange={(v) => set("pinned", v)} />
              <Label htmlFor="pin" className="text-sm">Pinned</Label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="flex items-center gap-1">Publish at <span className="text-[10px] text-[hsl(var(--muted-foreground))]">(event time)</span></Label><Input type="datetime-local" data-testid="ann-publish-at" value={f.publish_at || ""} onChange={(e) => set("publish_at", e.target.value)} /></div>
            <div><Label className="flex items-center gap-1">Expires at <span className="text-[10px] text-[hsl(var(--muted-foreground))]">(optional)</span></Label><Input type="datetime-local" value={f.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)} /></div>
          </div>
          <AudiencePicker eventId={eventId} value={f.audience_spec} onChange={(s) => set("audience_spec", s)} />
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" data-testid="ann-schedule" onClick={() => save(true)} disabled={saving}><CalendarClock className="h-4 w-4 mr-1" /> Save & Schedule</Button>
          <Button data-testid="ann-save" onClick={() => save(false)} disabled={saving}>{saving ? "Saving…" : "Save draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
